import {inject, injectable} from 'inversify';

import type {PaymentApiModel} from '../../../../api/payment';
import type {IApiDataMapper} from '../../../../core/ports/types';
import type {CurrencyModel} from '../../Currency/Currency.model';

import {CardMapperJrpc} from '../../Card/mappers/Card.mapper.jrpc';
import {CourseMapperJrpc} from '../../Course/mappers/Course.mapper.jrpc';
import {CurrencyFactory} from '../../Currency/Currency.factory';
import {DateTimeMapperJrpc} from '../../Date/mappers/DateTime.mapper.jrpc';
import {OfferMapperJrpc} from '../../Offer/mappers/Offer.mapper.jrpc';
import {PriceFactory} from '../../Price/Price.factory';
import {RefundMapperJrpc} from '../../Refund/mappers/Refund.mapper.jrpc';
import {UserMapperJrpc} from '../../User/mappers/User.mapper.jrpc';
import {PaymentFactory} from '../Payment.factory';
import {PaymentModel} from '../Payment.model';
import {PaymentExpandJrpc} from './Payment.expand.jrpc';

@injectable()
export class PaymentMapperJrpc
  implements
    IApiDataMapper<PaymentMapperJrpc.ApiData, PaymentModel.Props, PaymentModel>
{
  constructor(
    @inject(PaymentFactory)
    private readonly factory: PaymentFactory,
    @inject(PaymentExpandJrpc)
    public readonly expand: PaymentExpandJrpc,
    @inject(DateTimeMapperJrpc)
    private readonly dateTimeMapper: DateTimeMapperJrpc,
    @inject(CardMapperJrpc)
    private readonly cardMapper: CardMapperJrpc,
    @inject(UserMapperJrpc)
    private readonly userMapper: UserMapperJrpc,
    @inject(CourseMapperJrpc)
    private readonly courseMapper: CourseMapperJrpc,
    @inject(CurrencyFactory)
    private readonly currencyFactory: CurrencyFactory,
    @inject(PriceFactory)
    private readonly priceFactory: PriceFactory,
    @inject(RefundMapperJrpc)
    private readonly refundMapper: RefundMapperJrpc,
    @inject(OfferMapperJrpc)
    private readonly offerMapper: OfferMapperJrpc,
  ) {}

  mapDataToModel(apiData: PaymentMapperJrpc.ExpandedApiData): PaymentModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(
    apiData: PaymentMapperJrpc.ExpandedApiData,
  ): PaymentModel.Props {
    const currency = this.currencyFactory.create(apiData.currency);
    const coaches = this.coaches(apiData);

    return {
      amount: this.amount(apiData, currency),
      card: this.card(apiData),
      cardId: apiData.card_id,
      coaches,
      course: this.course(apiData),
      createdDate: this.dateTimeMapper.mapToModel(apiData.created),
      currency,
      endDate: this.dateTimeMapper.mapToModel(apiData.end_date),
      id: apiData._id,
      offer: this.offer(apiData),
      originalPaymentIds: apiData.original_payment_ids,
      paidDate: this.paidDate(apiData),
      processing: this.processing(apiData),
      refundedAmount: this.refundedAmount(apiData, currency),
      refunds: this.refunds(apiData),
      startDate: this.dateTimeMapper.mapToModel(apiData.start_date),
      status: this.status(apiData),
      transfers: this.transfers({apiData, coaches, currency}),
      type: this.type(apiData),
      updatedDate: this.dateTimeMapper.mapToModel(apiData.updated),
      user: this.userMapper.mapDataToModelOrEmpty(apiData.user),
      vendorPaymentId: apiData.payment_id,
    };
  }

  private amount(
    apiData: PaymentMapperJrpc.ExpandedApiData,
    currency: CurrencyModel,
  ): PaymentModel.Props['amount'] {
    return this.priceFactory.create(apiData.amount, currency);
  }

  private card(
    apiData: PaymentMapperJrpc.ExpandedApiData,
  ): PaymentModel.Props['card'] {
    return apiData.card ? this.cardMapper.mapDataToModel(apiData.card) : null;
  }

  private coaches(
    apiData: PaymentMapperJrpc.ExpandedApiData,
  ): PaymentModel.Props['coaches'] {
    return apiData.course
      ? apiData.course.expandedCoaches.map(coach =>
          this.userMapper.mapDataToModelOrEmpty(coach),
        )
      : [];
  }

  private course(
    apiData: PaymentMapperJrpc.ExpandedApiData,
  ): PaymentModel.Props['course'] {
    return apiData.course
      ? this.courseMapper.mapDataToModel(apiData.course)
      : null;
  }

  private offer(
    apiData: PaymentMapperJrpc.ExpandedApiData,
  ): PaymentModel.Props['offer'] {
    return apiData.offer
      ? this.offerMapper.mapDataToModel(apiData.offer)
      : null;
  }

  private paidDate(
    apiData: PaymentMapperJrpc.ExpandedApiData,
  ): PaymentModel.Props['paidDate'] {
    return apiData.paid ? this.dateTimeMapper.mapToModel(apiData.paid) : null;
  }

  private processing(
    apiData: PaymentMapperJrpc.ExpandedApiData,
  ): PaymentModel.Processing {
    switch (apiData.processing) {
      case 'external':
        return PaymentModel.Processing.EXTERNAL;
      default:
        return PaymentModel.Processing.INTERNAL;
    }
  }

  private refundedAmount(
    apiData: PaymentMapperJrpc.ExpandedApiData,
    currency: CurrencyModel,
  ): PaymentModel.Props['refundedAmount'] {
    return this.priceFactory.create(apiData.refunded_amount, currency);
  }

  private refunds(
    apiData: PaymentMapperJrpc.ExpandedApiData,
  ): PaymentModel.Props['refunds'] {
    return apiData.refunds
      .filter(Boolean)
      .map(refund =>
        this.refundMapper.mapDataToModel({
          ...refund,
          payment: null,
          refund_payment: null,
        }),
      )
      .sort((a, b) => (b.createdDate.gte(a.createdDate) ? 1 : -1));
  }

  private status(
    apiData: PaymentMapperJrpc.ExpandedApiData,
  ): PaymentModel.Status {
    switch (apiData.status) {
      case 'complete':
        return PaymentModel.Status.COMPLETE;
      case 'failed':
        return PaymentModel.Status.FAILED;
      case 'frozen':
        return PaymentModel.Status.FROZEN;
      case 'paid':
        return PaymentModel.Status.PAID;
      case 'processing':
        return PaymentModel.Status.PROCESSING;
      case 'requires_action':
        return PaymentModel.Status.REQUIRES_ACTION;
      case 'suspended':
        return PaymentModel.Status.SUSPENDED;
      default:
        return PaymentModel.Status.PENDING;
    }
  }

  private transfers({
    apiData,
    coaches,
    currency,
  }: {
    apiData: PaymentMapperJrpc.ExpandedApiData;
    coaches: PaymentModel.Props['coaches'];
    currency: CurrencyModel;
  }): PaymentModel.Props['transfers'] {
    const transfers: PaymentModel.Props['transfers'] = {};

    Object.entries(apiData.transfers).forEach(([coachId, transfer]) => {
      const coach = coaches.find(c => c.id === coachId) || null;

      transfers[coachId] = {
        amount: this.priceFactory.create(transfer.amount, currency),
        coach,
        id: transfer.transfer_id,
        portion: transfer.portion,
        reversedAmount: this.priceFactory.create(transfer.reversed_amount),
        transferredDate: transfer.transferred
          ? this.dateTimeMapper.mapToModel(transfer.transferred)
          : null,
      };
    });

    return transfers;
  }

  private type(apiData: PaymentMapperJrpc.ExpandedApiData): PaymentModel.Type {
    switch (apiData.type) {
      case 'offer':
        return PaymentModel.Type.OFFER;
      case 'program':
        return PaymentModel.Type.PROGRAM;
      case 'refund':
        return PaymentModel.Type.REFUND;
      case 'subscription':
        return PaymentModel.Type.SUBSCRIPTION;
    }
  }
}

export namespace PaymentMapperJrpc {
  export type ApiData = PaymentApiModel;

  export type ExpandedApiData = ApiData & PaymentExpandJrpc.ExpandedFields;
}
